import { c as _c } from "react/compiler-runtime";
import { useEffect } from 'react';
import { type WrappedTRPCClient } from '../helpers/trpcClientTypes';
import socket from '../socket/socket';

/** One runner the server reports as connected. Same shape the `runnerOnline`
 *  socket event carries, so both sources merge into one list. */
export type OnlineRunner = {
  socketId: string;
  runnerId: number;
  name: string;
  platform: string;
};

/**
 * Seeds the runner list from the server rather than waiting on the socket.
 *
 * The board used to learn about online runners from the `runnerOnline` push
 * alone. A handshake that failed to authenticate — an expired mobile token,
 * say — connects anyway and simply never receives that burst, so the board sat
 * on an empty list with nothing to ask and no error to report. Asking the
 * server directly makes the list recoverable.
 *
 * Web and mobile share this so the two lists cannot drift: the socket half
 * differs per platform (WebRTC), this half does not.
 *
 * `onRunners` receives the server's answer. It fires on mount, on every socket
 * (re)connect, and whenever `enabled` turns true. A failed ask answers null,
 * so a caller waiting on its first answer (a loader) is never left waiting.
 */
export function useOnlineRunners(client, enabled, onRunners) {
  const $ = _c(7);
  let t0;
  if ($[0] !== client || $[1] !== enabled || $[2] !== onRunners) {
    t0 = () => {
      if (!enabled) {
        return;
      }
      let ignore = false;
      const fetchOnline = () => {
        client.runners.online.query().then(t1 => {
          const [error, data] = t1;
          if (ignore) {
            return;
          }
          onRunners(error || !data ? null : data.runners);
        });
      };
      fetchOnline();
      socket.on("connect", fetchOnline);
      return () => {
        ignore = true;
        socket.off("connect", fetchOnline);
      };
    };
    $[0] = client;
    $[1] = enabled;
    $[2] = onRunners;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  let t1;
  if ($[4] !== client || $[5] !== enabled) {
    t1 = [client, enabled];
    $[4] = client;
    $[5] = enabled;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  useEffect(t0, t1);
}